import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import type { Product } from '../types/product';
import { useProducts } from './ProductsContext';
import { useBundles } from './BundlesContext';

const WISHLIST_STORAGE_KEY = 'palacesamples_wishlist';

function loadWishlistIds(): string[] {
  try {
    const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveWishlistIds(ids: string[]) {
  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

interface WishlistContextType {
  wishlistIds: string[];
  isInWishlist: (id: string) => boolean;
  addToWishlist: (id: string) => void;
  removeFromWishlist: (id: string) => void;
  toggleWishlist: (id: string) => void;
  wishlistProducts: (Product | { id: string; name: string; slug: string; type: 'bundle'; price: number; image?: string; theme: 'red' | 'blue' })[];
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const [wishlistIds, setWishlistIds] = useState<string[]>(loadWishlistIds);
  const { getProductById } = useProducts();
  const { getBundles } = useBundles();

  useEffect(() => {
    saveWishlistIds(wishlistIds);
  }, [wishlistIds]);

  const isInWishlist = useCallback(
    (id: string) => wishlistIds.includes(id),
    [wishlistIds]
  );

  const addToWishlist = useCallback((id: string) => {
    setWishlistIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const removeFromWishlist = useCallback((id: string) => {
    setWishlistIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const toggleWishlist = useCallback((id: string) => {
    setWishlistIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const wishlistProducts = wishlistIds
    .map((id) => {
      const product = getProductById(id);
      if (product) return product;
      const bundles = getBundles();
      const bundle = bundles.find((b) => b.id === id);
      if (bundle)
        return {
          id: bundle.id,
          name: bundle.name,
          slug: bundle.slug,
          type: 'bundle' as const,
          price: bundle.price,
          image: bundle.image,
          theme: bundle.theme,
        };
      return null;
    })
    .filter(Boolean) as WishlistContextType['wishlistProducts'];

  return (
    <WishlistContext.Provider
      value={{
        wishlistIds,
        isInWishlist,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        wishlistProducts,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
}
